import {
  ErrCode as RestErrorCode,
  RestError
} from "src/gen/schema/common/error";
import {
  Error as SyncFollowError,
  ErrorCode as SyncFollowErrorCode
} from "src/gen/schema/syncfollow/syncfollow";

import { logger } from "./globals";

const UNKNOWN = "Unknown Error";

export enum ApiErrorSource {
  Rest = "rest",
  Client = "client",
  SyncFollow = "syncfollow"
}

type ApiErrorOptions = {
  source: ApiErrorSource;
  message: string;
  debugMessage?: string;
  trackingId?: string;
  criticalError?: boolean;
  httpStatusCode?: number;
  restErrorCode?: RestErrorCode;
  syncFollowErrorCode?: SyncFollowErrorCode;
};

export type FieldError = {
  field: string;
  value: string;
  message: string;
};

/**
 * Normalized UI API error object.
 *
 * The constructor is private so TypeScript rejects direct `new ApiError(...)`
 * usage outside this class. Keep creation behind factory methods so backend
 * field mapping, user-safe fallback messages, field errors, and logging stay
 * centralized.
 */
export class ApiError {
  private message: string;
  private debugMessage: string | undefined;
  private source: ApiErrorSource;
  private trackingId: string | undefined;
  private criticalError: boolean;
  private httpStatusCode: number | undefined;
  private restErrorCode: RestErrorCode | undefined;
  private syncFollowErrorCode: SyncFollowErrorCode | undefined;
  private fieldErrors: Map<string, FieldError> = new Map<string, FieldError>();

  /**
   * Private by design.
   *
   * Only factory methods in this class should call this constructor. That gives
   * TypeScript compile-time enforcement: external `new ApiError(...)` calls
   * fail, so callers cannot bypass source-specific mapping or logging.
   *
   * Keep this constructor limited to assigning normalized fields. Converting
   * REST, sync-follow, or local fallback errors belongs in the `from*` factory
   * methods.
   */
  private constructor(options: ApiErrorOptions) {
    this.source = options.source;
    this.message = options.message || UNKNOWN;
    this.debugMessage = options.debugMessage;
    this.trackingId = options.trackingId;
    this.criticalError = options.criticalError ?? false;
    this.httpStatusCode = options.httpStatusCode;
    this.restErrorCode = options.restErrorCode;
    this.syncFollowErrorCode = options.syncFollowErrorCode;
  }

  /**
   * Helper method to create an ApiError from a simple message string.
   * @param message
   * @param source
   */
  static fromMessage(
    message: string,
    source: ApiErrorSource = ApiErrorSource.Client
  ) {
    const apiError = new ApiError({
      source: source,
      message
    });
    apiError.log();
    return apiError;
  }

  /**
   * Helper method to create an ApiError from a debug message string and optional user-friendly message.
   * The debug message will be logged for troubleshooting, while the user message can be displayed in the UI.
   * @param debugMessage
   * @param source
   * @param userMessage
   */
  static fromDebugMessage(
    debugMessage: string,
    source: ApiErrorSource = ApiErrorSource.Client,
    userMessage: string = UNKNOWN
  ) {
    const apiError = new ApiError({
      source: source,
      message: userMessage,
      debugMessage
    });
    apiError.log();
    return apiError;
  }

  /**
   * Factory method to create an ApiError from a RestError object. This will map RestError fields to ApiError and log the error.
   * @param e
   */
  static fromRestError(e: RestError) {
    const apiError = new ApiError({
      source: ApiErrorSource.Rest,
      message: e.message,
      trackingId: e.trackingId || undefined,
      httpStatusCode: e.status || undefined,
      restErrorCode: e.errCode,
      criticalError: e.critical
    });
    if (e.field) {
      apiError.addFieldError(e);
    }
    apiError.log();
    return apiError;
  }

  /**
   * Factory method to create an ApiError from a SyncFollowError object. This will map SyncFollowError fields to ApiError and log the error.
   * @param e
   */
  static fromSyncFollowError(e: SyncFollowError) {
    const apiError = new ApiError({
      source: ApiErrorSource.SyncFollow,
      message: e.errorMessage,
      trackingId: e.syncId || undefined,
      httpStatusCode: e.httpStatusCode || undefined,
      syncFollowErrorCode: e.errorCode,
      criticalError: e.fatalError
    });
    apiError.log();
    return apiError;
  }

  private addFieldError(error: RestError) {
    const fieldError: FieldError = {
      field: error.field,
      value: error.value,
      message: error.message
    };
    this.fieldErrors.set(error.field, fieldError);
  }

  /**
   * Centralized logging for all ApiErrors.
   * This is called by all factory methods after creating the ApiError, ensuring consistent logging of all relevant fields.
   */
  private log() {
    logger.error("Error Received: ", {
      source: this.source,
      message: this.message,
      detailedMessage: this.debugMessage,
      trackingId: this.trackingId,
      criticalError: this.criticalError,
      httpStatusCode: this.httpStatusCode,
      restErrorCode: this.restErrorCode,
      syncFollowErrorCode: this.syncFollowErrorCode,
      fieldErrors: Array.from(this.fieldErrors.values())
    });
  }

  getSource() {
    return this.source;
  }

  getTrackingId() {
    return this.trackingId;
  }

  getMessage() {
    return this.message || UNKNOWN;
  }

  getHttpStatusCode() {
    return this.httpStatusCode;
  }

  getSyncFollowErrorCode() {
    return this.syncFollowErrorCode;
  }

  getRestErrorCode() {
    return this.restErrorCode;
  }

  isCriticalError() {
    return this.criticalError;
  }

  getFieldErrors() {
    return this.fieldErrors;
  }
}

/**
 * Type guard to check if the error is an ApiError
 */
export function isApiError<E extends ApiError>(test: unknown): test is E {
  return (test as E) instanceof ApiError;
}
